/**
 * Registration Page — Multi-Step
 * CCH Care - Version 1.3
 *
 * Step 1: Your Account (admin credentials)
 * Step 2: Your Organization (org + facility basics)
 * Step 3: Success
 *
 * v1.3 changes:
 * - State <select> dropdown visibility fix (dark mode)
 *   - Added colorScheme: 'dark' so browser uses dark UI chrome for the popup
 *   - Explicit dark background/white text on each <option> as fallback
 *
 * v1.2 changes:
 * - Admin account collected first — this is always the person registering
 * - Organization / facility collected second — minimal fields only
 * - Full facility address deferred to Setup Wizard after login
 * - contact_email / contact_phone removed — admin_email used as billing contact
 * - State field optional, used for timezone context only
 *
 * ESLint: label-has-associated-control, no-autofocus, no-unescaped-entities
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register } from '../../api/auth';
import { COLORS } from '../../config/constants';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

// Dark-mode-safe option styling — needed because native <option> elements
// don't reliably inherit the parent <select>'s colors in the popup.
const optionStyle = {
  background: '#0B1929',
  color: '#fff',
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep]             = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);

  const [form, setForm] = useState({
    admin_first_name:       '',
    admin_last_name:        '',
    admin_email:            '',
    admin_password:         '',
    admin_password_confirm: '',
    organization_name:      '',
    facility_name:          '',
    facility_state:         '',
    bed_count:              '',
  });

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  }

  function validateStep1() {
    if (!form.admin_first_name.trim()) return 'First name is required';
    if (!form.admin_last_name.trim())  return 'Last name is required';
    if (!form.admin_email.trim())      return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email)) return 'Invalid email format';
    if (!form.admin_password)          return 'Password is required';
    if (form.admin_password.length < 12) return 'Password must be at least 12 characters';
    if (!/[A-Z]/.test(form.admin_password)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(form.admin_password)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(form.admin_password)) return 'Password must contain a number';
    if (!/[^A-Za-z0-9]/.test(form.admin_password)) return 'Password must contain a special character';
    if (form.admin_password !== form.admin_password_confirm) return 'Passwords do not match';
    return null;
  }

  function validateStep2() {
    if (!form.organization_name.trim()) return 'Organization name is required';
    if (!form.facility_name.trim())     return 'Facility name is required';
    if (!form.bed_count || parseInt(form.bed_count) < 1) return 'Number of beds is required';
    return null;
  }

  function nextStep() {
    const err = step === 1 ? validateStep1() : validateStep2();
    if (err) { setError(err); return; }
    setStep(step + 1);
  }

  async function handleSubmit() {
    const err = validateStep2();
    if (err) { setError(err); return; }

    setSubmitting(true);
    setError(null);

    try {
      const data = await register({
        admin_first_name:  form.admin_first_name,
        admin_last_name:   form.admin_last_name,
        admin_email:       form.admin_email,
        admin_password:    form.admin_password,
        organization_name: form.organization_name,
        facility_name:     form.facility_name,
        facility_state:    form.facility_state || undefined,
        bed_count:         parseInt(form.bed_count),
      });
      setSuccess(data);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Shared styles ─────────────────────────────────────────
  const inputStyle = {
    width: '100%', padding: '14px 16px', fontSize: 15,
    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
    background: 'rgba(255,255,255,0.06)', color: '#fff',
    outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle = {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: '#93C5FD', marginBottom: 6,
  };
  const rowStyle  = { marginBottom: 20 };
  const splitRow  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 };
  const hintStyle = {
    padding: '10px 14px', borderRadius: 8, marginBottom: 24,
    background: 'rgba(27,107,147,0.2)', border: '1px solid rgba(27,107,147,0.3)',
    fontSize: 13, color: '#93C5FD', lineHeight: 1.6,
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0B1929', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        maxWidth: 540, width: '100%', background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: 40,
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: COLORS.primary,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
          }}>
            CCH Care
          </div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>
            {step === 3 ? 'Registration Complete!' : 'Start Your Free Trial'}
          </div>
          {step < 3 && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
              {[1, 2].map(s => (
                <div key={s} style={{
                  width: s === step ? 32 : 10, height: 10, borderRadius: 5,
                  background: s <= step ? COLORS.primary : 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s',
                }} />
              ))}
            </div>
          )}
          {step < 3 && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#64748B' }}>
              Step {step} of 2 &mdash; {step === 1 ? 'Your Account' : 'Your Organization'}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 10, marginBottom: 20,
            background: 'rgba(231,76,60,0.15)', border: '1px solid rgba(231,76,60,0.3)',
            color: '#FC8181', fontSize: 14, lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        {/* ── Step 1: Your Account ── */}
        {step === 1 && (
          <>
            <div style={hintStyle}>
              👤 This creates your administrator account. You&apos;ll use this email and
              password to log in and manage staff, residents, and facility settings.
            </div>

            <div style={splitRow}>
              <div>
                <label htmlFor="admin-first-name" style={labelStyle}>First Name *</label>
                <input
                  id="admin-first-name"
                  style={inputStyle}
                  value={form.admin_first_name}
                  onChange={e => update('admin_first_name', e.target.value)}
                  placeholder="Jane"
                />
              </div>
              <div>
                <label htmlFor="admin-last-name" style={labelStyle}>Last Name *</label>
                <input
                  id="admin-last-name"
                  style={inputStyle}
                  value={form.admin_last_name}
                  onChange={e => update('admin_last_name', e.target.value)}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div style={rowStyle}>
              <label htmlFor="admin-email" style={labelStyle}>Email Address *</label>
              <input
                id="admin-email"
                style={inputStyle}
                type="email"
                autoComplete="username"
                value={form.admin_email}
                onChange={e => update('admin_email', e.target.value)}
                placeholder="you@yourfacility.com"
              />
            </div>

            <div style={rowStyle}>
              <label htmlFor="admin-password" style={labelStyle}>Password *</label>
              <input
                id="admin-password"
                style={inputStyle}
                type="password"
                autoComplete="new-password"
                value={form.admin_password}
                onChange={e => update('admin_password', e.target.value)}
                placeholder="Minimum 12 characters"
              />
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 6, lineHeight: 1.6 }}>
                Must include: uppercase, lowercase, number, and special character
              </div>
            </div>

            <div style={rowStyle}>
              <label htmlFor="admin-password-confirm" style={labelStyle}>Confirm Password *</label>
              <input
                id="admin-password-confirm"
                style={inputStyle}
                type="password"
                autoComplete="new-password"
                value={form.admin_password_confirm}
                onChange={e => update('admin_password_confirm', e.target.value)}
                placeholder="Re-enter your password"
              />
            </div>

            <button onClick={nextStep} style={btnPrimary}>
              Continue &rarr;
            </button>

            <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: '#64748B' }}>
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                style={{ background: 'none', border: 'none', color: '#93C5FD', cursor: 'pointer', fontSize: 13, padding: 0 }}
              >
                Sign in
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Your Organization ── */}
        {step === 2 && (
          <>
            <div style={hintStyle}>
              🏥 Tell us about your organization. You can complete your full facility
              address, add units, rooms, and staff after logging in using the Setup Guide.
            </div>

            <div style={rowStyle}>
              <label htmlFor="org-name" style={labelStyle}>Organization Name *</label>
              <input
                id="org-name"
                style={inputStyle}
                value={form.organization_name}
                onChange={e => update('organization_name', e.target.value)}
                placeholder="e.g. Sunrise Senior Care"
              />
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
                The name of your company or operating entity.
              </div>
            </div>

            <div style={rowStyle}>
              <label htmlFor="facility-name" style={labelStyle}>Facility Name *</label>
              <input
                id="facility-name"
                style={inputStyle}
                value={form.facility_name}
                onChange={e => update('facility_name', e.target.value)}
                placeholder="e.g. Sunrise North Campus"
              />
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
                The name of the physical care location.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label htmlFor="facility-state" style={labelStyle}>
                  State <span style={{ fontWeight: 400, color: '#64748B' }}>(optional)</span>
                </label>
                <select
                  id="facility-state"
                  style={{ ...inputStyle, cursor: 'pointer', colorScheme: 'dark' }}
                  value={form.facility_state}
                  onChange={e => update('facility_state', e.target.value)}
                >
                  <option value="" style={optionStyle}>&mdash; Select state</option>
                  {US_STATES.map(s => (
                    <option key={s} value={s} style={optionStyle}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bed-count" style={labelStyle}>Number of Beds *</label>
                <input
                  id="bed-count"
                  style={inputStyle}
                  type="number"
                  min="1"
                  max="9999"
                  value={form.bed_count}
                  onChange={e => update('bed_count', e.target.value)}
                  placeholder="e.g. 60"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(1)} style={btnSecondary}>&larr; Back</button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ ...btnPrimary, flex: 2, opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? 'Creating Account...' : 'Start Free Trial'}
              </button>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: '#64748B', textAlign: 'center', lineHeight: 1.6 }}>
              By registering you agree to our Terms of Service and Privacy Policy.
              Your 14-day trial includes full access to all features. No credit card required.
            </div>
          </>
        )}

        {/* ── Step 3: Success ── */}
        {step === 3 && success && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <p style={{ color: '#94A3B8', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
              Welcome, <strong style={{ color: '#fff' }}>{form.admin_first_name}</strong>! Your
              organization <strong style={{ color: '#fff' }}>{form.organization_name}</strong> has
              been created with a 14-day free trial. A welcome email has been sent
              to <strong style={{ color: '#fff' }}>{form.admin_email}</strong>.
            </p>
            <div style={{
              padding: 20, background: 'rgba(46,204,113,0.1)', borderRadius: 12,
              border: '1px solid rgba(46,204,113,0.2)', marginBottom: 16,
            }}>
              <div style={{ fontSize: 14, color: '#2ECC71', fontWeight: 600, marginBottom: 4 }}>
                Trial expires
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {new Date(success.trial_ends_at).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                })}
              </div>
            </div>
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 24,
              background: 'rgba(27,107,147,0.2)', border: '1px solid rgba(27,107,147,0.3)',
              fontSize: 13, color: '#93C5FD', lineHeight: 1.6, textAlign: 'left',
            }}>
              <strong>After logging in:</strong> use the Setup Guide on your dashboard to add
              your facility address, units, rooms, residents, and staff.
            </div>
            <button onClick={() => navigate('/login')} style={btnPrimary}>
              Sign In to Get Started &rarr;
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Button styles ─────────────────────────────────────────────
const btnPrimary = {
  width: '100%', padding: 16, border: 'none', borderRadius: 10,
  background: '#1B6B93', color: '#fff', fontSize: 16,
  fontWeight: 700, cursor: 'pointer', flex: 1,
};
const btnSecondary = {
  padding: 16, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
  background: 'transparent', color: '#94A3B8', fontSize: 15,
  fontWeight: 600, cursor: 'pointer', flex: 1,
};
